import { useEffect, useRef } from "react";

let pendingAuthMessageListener = null;

const stopListeningForAuthMessage = () => {
  if (pendingAuthMessageListener) {
    window.removeEventListener("message", pendingAuthMessageListener);
    pendingAuthMessageListener = null;
  }
};

const useWidgetLogin = ({
  confirmId,
  ticketReservationToken,
  userContext,
  onAuthSuccess,
  rootUrl
}) => {
  const isLoggedIn = userContext && userContext.id;
  const authInProgressRef = useRef(false);

  useEffect(() => {
    const checkAuthStatus = async () => {
      if (!authInProgressRef.current || isLoggedIn) {
        return;
      }

      try {
        const response = await fetch(
          `${rootUrl}widget_auth/user_context?confirm_id=${confirmId}`,
          { credentials: "include" }
        );
        const data = await response.json();

        if (data.authenticated && data.userContext) {
          authInProgressRef.current = false;
          onAuthSuccess({ userContext: data.userContext });
        }
      } catch (e) {
        console.warn("Auth status check failed:", e);
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkAuthStatus();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [confirmId, rootUrl, onAuthSuccess, isLoggedIn]);

  const handleLoginClick = (e) => {
    e.preventDefault();

    authInProgressRef.current = true;

    const origin = window.location.origin;
    const params = new URLSearchParams({
      confirm_id: confirmId,
      origin: origin
    });

    if (ticketReservationToken) {
      params.append("ticket_reservation_token", ticketReservationToken);
    }

    const loginUrl = `${rootUrl}widget_auth/login?${params.toString()}`;

    const width = 500;
    const height = 600;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    const popup = window.open(
      loginUrl,
      "opendate_auth",
      `width=${width},height=${height},left=${left},top=${top},scrollbars=yes`
    );

    if (!popup || popup.closed || typeof popup.closed === "undefined") {
      alert("Please allow popups for this site to sign in.");
      authInProgressRef.current = false;
      return;
    }

    const handleMessage = (event) => {
      if (event.origin !== new URL(rootUrl).origin) {
        return;
      }

      if (event.data && event.data.from === "widget_auth_popup") {
        stopListeningForAuthMessage();
        authInProgressRef.current = false;

        if (event.data.command === "widgetAuthSuccess") {
          onAuthSuccess(event.data.data);
        } else if (event.data.command === "widgetAuthFailure") {
          console.error("Auth failed:", event.data.data.error);
        }
      }
    };

    stopListeningForAuthMessage();
    pendingAuthMessageListener = handleMessage;
    window.addEventListener("message", handleMessage);
  };

  return { isLoggedIn, handleLoginClick };
};

export default useWidgetLogin;
